<template>
	<span :aria-label="cleanText" ref="encrypt-and-decrypt" :class="{ 'encrypting pointer-events-none': isEncrypting }"><slot></slot></span>
</template>

<script setup>
import { ref, useTemplateRef, onMounted, onUnmounted } from 'vue'
import { ScrollTrigger } from "gsap/ScrollTrigger";

const props = defineProps({
	maxEncryptedChars: {
		type: Number,
		default: 8
	},
	changesBeforeProgress: {
		type: Number,
		default: 2
	},
	encryptionChars: {
		type: String,
		default: 'mixedcase'
	},
	frameDelay: {
		type: Number,
		default: 25
	},
	scroll: {
		type: Boolean,
		default: false
	},
	hover: {
		type: Boolean,
		default: true
	}
})

const emit = defineEmits(['encryptStart', 'encryptEnd']);

const encryptRef = useTemplateRef('encrypt-and-decrypt');
const text = ref('');
const cleanText = ref('');
onMounted(() => {
	text.value = encryptRef.value.innerHTML;
	cleanText.value = text.value.replace(/<[^>]+>/g, '');
})

const encryptionChars = function() {
	if (props.encryptionChars == 'uppercase') {
		return 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789@#$%&';
	} else if (props.encryptionChars == 'lowercase') {
		return 'abcdefghijklmnopqrstuvwxyz0123456789@#$%&';
	} else if (props.encryptionChars == 'mixedcase') {
		return 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789@#$%&';
	} else {
		return props.encryptionChars;
	}
}

const config = {
	maxEncryptedChars: props.maxEncryptedChars, // how many characters should be encrypted at most at any given time
	changesBeforeProgress: props.changesBeforeProgress, // how many times should a character change before moving to the next
	encryptionChars: encryptionChars(), // characters to use for encryption
	frameDelay: props.frameDelay, // milliseconds between frames
};

const isEncrypting = ref(false);

const animateEncryptDecryptText = async() => {
	if (isEncrypting.value) return;

	isEncrypting.value = true;

	const element = encryptRef.value;

	const getRandomChar = () => {
		return config.encryptionChars[Math.floor(Math.random() * config.encryptionChars.length)];
	};

	// Get the original text
	let originalText = text.value;

	// Add <wbr> after spaces, but not within HTML tags
	originalText = originalText.replace(/(<[^>]*>)|(\s)/g, (match, tag) => {
		return tag ? match : match + '<wbr>';
	});

	// Store HTML elements and their positions
	const htmlElements = [];
	let searchIndex = 0;

	// Find and store HTML elements
	while (true) {
		const startTagIndex = originalText.indexOf('<', searchIndex);
		if (startTagIndex === -1) break;

		const endTagIndex = originalText.indexOf('>', startTagIndex);
		if (endTagIndex === -1) break;

		htmlElements.push({
			element: originalText.substring(startTagIndex, endTagIndex + 1),
			position: startTagIndex
		});

		searchIndex = endTagIndex + 1;
	}

	// Remove HTML elements from text
	const cleanText = originalText.replace(/<[^>]+>/g, '');

	// Prepare states array
	let states = [cleanText];

	// Calculate actual maximum encryption count based on text length
	const actualMaxEncryptedChars = Math.min(config.maxEncryptedChars, cleanText.length);

	// First phase: ramping up
	for (let startPos = 0; startPos < actualMaxEncryptedChars; startPos++) {
		for (let change = 0; change < config.changesBeforeProgress; change++) {
			let newState = cleanText.split('');
			for (let i = 0; i <= startPos; i++) {
				newState[i] = getRandomChar();
			}
			states.push(newState.join(''));
		}
	}

	// Middle phase: moving window of actualMaxEncryptedChars (only if string is long enough)
	if (cleanText.length > actualMaxEncryptedChars) {
		for (let startPos = 1; startPos <= cleanText.length - actualMaxEncryptedChars; startPos++) {
			for (let change = 0; change < config.changesBeforeProgress; change++) {
				let newState = cleanText.split('');
				for (let i = startPos; i < startPos + actualMaxEncryptedChars; i++) {
					newState[i] = getRandomChar();
				}
				states.push(newState.join(''));
			}
		}
	}

	// Final phase: ramping down (only if we have more than one character)
	if (cleanText.length > 1) {
		for (let remainingChars = actualMaxEncryptedChars - 1; remainingChars > 0; remainingChars--) {
			const startPos = cleanText.length - remainingChars;
			for (let change = 0; change < config.changesBeforeProgress; change++) {
				let newState = cleanText.split('');
				for (let i = startPos; i < cleanText.length; i++) {
					newState[i] = getRandomChar();
				}
				states.push(newState.join(''));
			}
		}
	}

	// Add final state if not already present
	if (states[states.length - 1] !== cleanText) {
		states.push(cleanText);
	}

	// Re-insert HTML elements into each state
	states = states.map(state => {
		let stateWithHtml = state.split('');
		let offset = 0;
		htmlElements.forEach(({element, position}) => {
			stateWithHtml.splice(position - offset, 0, element);
			offset += element.length - 1;
		});
		return stateWithHtml.join('');
	});

	return new Promise((resolve) => {
		// Animate through the states
		let currentFrame = 0;

		const animate = () => {
			if (currentFrame < states.length) {
				element.innerHTML = states[currentFrame];
				currentFrame++;
				setTimeout(animate, config.frameDelay);
			} else {
				resolve();
				isEncrypting.value = false;
			}
		};

		// Start the animation
		animate();
	});
}

const encryptDecryptText = async () => {
	if (encryptRef.value) {
		try {
			emit('encryptStart');
			await animateEncryptDecryptText();
			emit('encryptEnd');
		} catch (error) {
			console.log(error);
		}
	}
};

let targetElement = null;
let scrollTrigger = null;

onMounted(() => {
	// Find the closest parent with the data attribute
	const parentTrigger = encryptRef.value.closest('[data-encrypt-decrypt-trigger]')

	// Get the target element (either parent or root)
	targetElement = parentTrigger || encryptRef.value

	if (props.scroll) {
		scrollTrigger = ScrollTrigger.create({
			trigger: targetElement,
			start: 'bottom 95%',
			onEnter: () => {
				encryptDecryptText();
			},
		});
	} else if (props.hover) {
		// Add the event listener
		targetElement.addEventListener('pointerenter', encryptDecryptText)
	}
});

// Don't forget to clean up the event listener
onUnmounted(() => {
	if (props.scroll) {
		scrollTrigger.kill();
	} else if (props.hover) {
		targetElement.removeEventListener('pointerenter', encryptDecryptText)
	}
});

defineExpose({
	encryptDecryptText
})
</script>
